import React, { useState, useRef, useEffect } from "react";
import { View, Text, Alert, ScrollView, Linking } from "react-native";
import { Audio } from "expo-av";
import * as DocumentPicker from "expo-document-picker";
import { Button } from "@/elements/components/ui/button";
import { AppBar } from "@/elements/components/ui/app-bar";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/elements/components/utils/convex-api";
import { router, useLocalSearchParams } from "expo-router";
import { Id } from "@teachloop/backend/_generated/dataModel";
import Waveform from "@/elements/components/session/waveform";
import Toast from "react-native-toast-message";
import { useConvexAction, useConvexMutation } from "@convex-dev/react-query";
import LessonSubmittedModal from "../modals/lesson-submitted-modal";
import goBack from "../utils/go-back";

type FileMeta = {
  uri: string;
  name: string;
  mimeType: string;
  size: number;
};

function TutorLessonRecording() {
  const { sessionId } = useLocalSearchParams<{ sessionId: string }>();

  const [isRecording, setIsRecording] = useState(false);
  const [recording, setRecording] = useState<Audio.Recording | null>(null);
  const [audioUri, setAudioUri] = useState<string | null>(null);
  const [audioSource, setAudioSource] = useState<"none" | "recorded" | "file">(
    "none"
  );
  const [file, setFile] = useState<FileMeta | null>(null);
  const [time, setTime] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isLessonSubmittedModalVisible, setIsLessonSubmittedModalVisible] =
    useState(false);

  const { mutateAsync: generateVoiceUploadUrl } = useMutation({
    mutationFn: useConvexAction(api.tutor.home.actions.generateVoiceUploadUrl),
  });
  const { mutateAsync: createSessionRecording } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.home.mutations.createInPersonSessionRecording
    ),
  });

  const base64ToBlob = (base64Uri: string) => {
    const [meta, data] = base64Uri.split(",");
    const mime = meta?.match(/:(.*?);/)?.[1];
    if (!mime) throw new Error("Invalid MIME type");
    const binary = atob(data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  };

  const startTimer = () =>
    (intervalRef.current = setInterval(() => setTime((t) => t + 1), 1000));
  const stopTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  };
  const formatTime = (s: number) =>
    [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
      .map((n) => String(n).padStart(2, "0"))
      .join(":");

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async () => {
      const fileUri = audioUri || file?.uri;
      if (!fileUri) throw new Error("No file to upload");

      // Prepare file

      let blob;

      console.log('=== FILE UPLOAD DEBUG ===');
      console.log('fileUri:', fileUri);
      console.log('file:', file);
      console.log('file instanceof File:', file instanceof File);
      console.log('fileUri starts with data:', fileUri.startsWith("data:"));
      console.log('fileUri starts with blob/http:', fileUri.startsWith("blob:") || fileUri.startsWith("http"));

      if (fileUri.startsWith("data:")) {
        // base64 data URL
        console.log('Taking data: path');
        blob = base64ToBlob(fileUri);
      } else if (fileUri.startsWith("blob:") || fileUri.startsWith("http")) {
        // blob: URL or http(s)
        console.log('Taking blob/http path');
        const res = await fetch(fileUri);
        blob = await res.blob();
      } else if (file instanceof File) {
        // If you have a real File object already
        console.log('Taking File object path');
        blob = file;
      } else {
        console.log('Taking error path');
        throw new Error("Unsupported file URI format on this platform");
      }

      console.log('Final blob:', blob);
      console.log('Blob size:', blob.size);
      console.log('Blob type:', blob.type);

      const size = file?.size || blob.size;
      const name = file?.name || `lesson-${Date.now()}.m4a`;
      const mimeType = file?.mimeType || "audio/mp4";

      // Get signed URL
      const uploadData = await generateVoiceUploadUrl({
        fileName: name,
        fileSize: size,
        mimeType,
        sessionId: sessionId as Id<"sessions">,
      });

      // Upload
      const uploadRes = await fetch(uploadData.uploadUrl, {
        method: "PUT",
        body: blob,
        headers: { "Content-Type": mimeType },
      });

      if (!uploadRes.ok) throw new Error("Upload failed");
      await createSessionRecording({ 
        sessionId: sessionId as Id<"sessions">,
        fileName: uploadData.fileName,
        source: "app",
        type: "audio"
      });
      setIsLessonSubmittedModalVisible(true);
    },
    onSuccess: () => {
      reset();
      Toast.show({ type: "success", text1: "Upload complete" });
    },
    onError: () =>
      Alert.alert(
        "Upload Failed",
        "Failed to upload recording. Please try again."
      ),
  });

  // Recording controls
  const startRecording = async () => {
    const { status, canAskAgain } = await Audio.getPermissionsAsync();

    if (status === "granted") {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });
      const { recording: rec } = await Audio.Recording.createAsync(
        Audio.RecordingOptionsPresets.HIGH_QUALITY
      );

      setRecording(rec);
      setIsRecording(true);
      setAudioSource("recorded");
      setAudioUri(null);
      setFile(null);
      setTime(0);
      startTimer();
      return;
    }

    if (canAskAgain) {
      const { status: newStatus } = await Audio.requestPermissionsAsync();
      if (newStatus === "granted") {
        await Audio.setAudioModeAsync({
          allowsRecordingIOS: true,
          playsInSilentModeIOS: true,
        });
        const { recording: rec } = await Audio.Recording.createAsync(
          Audio.RecordingOptionsPresets.HIGH_QUALITY
        );

        setRecording(rec);
        setIsRecording(true);
        setAudioSource("recorded");
        setAudioUri(null);
        setFile(null);
        setTime(0);
        startTimer();
      } else {
        Toast.show({
          type: "error",
          text1: "Permission Required",
          text2: "Audio recording permission is required.",
        });
      }
    } else {
      Alert.alert(
        "Permission Denied",
        "Microphone permission is required to record lessons. Please enable it in your device settings.",
        [
          { text: "Cancel", style: "cancel" },
          { text: "Open Settings", onPress: () => Linking.openSettings() }
        ]
      );
    }
  };

  const pauseRecording = async () => {
    if (!recording) return;
    await recording.pauseAsync();
    setIsRecording(false);
    stopTimer();
  };

  const resumeRecording = async () => {
    if (!recording) return;
    await recording.startAsync();
    setIsRecording(true);
    startTimer();
  };

  const stopRecording = async () => {
    if (!recording) return;
    stopTimer();
    await recording.stopAndUnloadAsync();
    const uri = recording.getURI();
    setAudioUri(uri || null);
    setRecording(null);
    setIsRecording(false);
  };

  const pickFile = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: ["audio/mpeg", "audio/mp3", "audio/mp4", "audio/x-m4a", "audio/wav", "audio/webm", "audio/aac"],
      multiple: false,
      copyToCacheDirectory: true,
    });
    if (!result.canceled && result.assets[0]) {
      const f = result.assets[0];
      setFile({
        uri: f.uri,
        name: f.name,
        mimeType: f.mimeType || "audio/mp4",
        size: f.size || 0,
      });
      setAudioSource("file");
      setAudioUri(null);
      setRecording(null);
      setIsRecording(false);
    }
  };

  const reset = () => {
    stopTimer();
    setRecording(null);
    setAudioUri(null);
    setFile(null);
    setIsRecording(false);
    setAudioSource("none");
    setTime(0);
  };

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, []);

  // Derived flags
  const isPaused =
    audioSource === "recorded" && !isRecording && recording !== null;
  const readyToUpload =
    (audioSource === "recorded" && audioUri) || audioSource === "file";

  return (
    <View className="flex-1 bg-teachLoop-accentPrimary">
      <AppBar>
        <AppBar.Icon pathClassName="stroke-white" onPress={goBack} />
        <AppBar.Title className="text-white">Back</AppBar.Title>
      </AppBar>

      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5 gap-2">
            <Text className="font-zilla-slab-medium text-[28px] text-teachLoop-creamLight">
              Live lesson in progress...
            </Text>
            <Text className="font-geologica-regular text-xl text-teachLoop-creamLight">
              This session is being recorded
            </Text>

            <View className="h-[72px]" />

            <View className="items-center gap-5">
              <Waveform recording={isRecording} hasSelectedFile={!!file} />
              <Text className="font-geologica-medium text-[32px] text-teachLoop-creamLight text-center">
                {file ? file.name : formatTime(time)}
              </Text>
            </View>

            <View className="h-[72px]" />

            <View className="gap-6 items-center">
              {/* Idle */}
              {audioSource === "none" && !isRecording && (
                <>
                  <Button
                    size="md"
                    outline
                    intent="light"
                    className="border"
                    onPress={startRecording}
                  >
                    <Button.Text size="md" intent="light">
                      Start recording
                    </Button.Text>
                  </Button>
                  <Button
                    size="md"
                    outline
                    intent="light"
                    className="border"
                    onPress={pickFile}
                  >
                    <Button.Text size="md" intent="light">
                      Pick recording
                    </Button.Text>
                  </Button>
                </>
              )}

              {/* Recording */}
              {isRecording && (
                <Button
                  size="md"
                  outline
                  intent="light"
                  className="border"
                  onPress={pauseRecording}
                >
                  <Button.Text size="md" intent="light">
                    Pause recording
                  </Button.Text>
                </Button>
              )}

              {/* Paused */}
              {isPaused && (
                <>
                  <Button
                    size="md"
                    outline
                    intent="light"
                    className="border"
                    onPress={resumeRecording}
                  >
                    <Button.Text size="md" intent="light">
                      Resume recording
                    </Button.Text>
                  </Button>
                  <Button size="md" intent="light" onPress={stopRecording}>
                    <Button.Text size="md" intent="primary">
                      Done recording
                    </Button.Text>
                  </Button>
                </>
              )}

              {/* Ready to upload or file selected */}
              {readyToUpload && (
                <>
                  <Button
                    size="md"
                    intent="light"
                    onPress={() => uploadMutation.mutate()}
                    disabled={uploadMutation.isPending}
                  >
                    <Button.Text size="md" intent="primary">
                      {uploadMutation.isPending
                        ? "Uploading..."
                        : audioSource === "recorded"
                          ? "End lesson"
                          : "Upload recording"}
                    </Button.Text>
                  </Button>
                  <Button
                    size="md"
                    outline
                    intent="light"
                    className="border"
                    onPress={reset}
                    disabled={uploadMutation.isPending}
                  >
                    <Button.Text size="md" intent="light">
                      Clear recording
                    </Button.Text>
                  </Button>
                </>
              )}
            </View>
          </View>
        </ResponsiveContent>
      </ScrollView>

      <LessonSubmittedModal
        isVisible={isLessonSubmittedModalVisible}
        onClose={() => {
          setIsLessonSubmittedModalVisible(false);
          router.replace("/(app)/home");
        }}
      />
    </View>
  );
}

export default TutorLessonRecording;
